/**
 * Widget State Machine
 * States: idle → connecting → text-chat ⇌ voice → idle
 *
 * Text mode: data channel only (mic OFF, agent audio MUTED)
 * Voice mode: mic ON, agent audio UN-MUTED, text input hidden
 */
import { ApiClient } from './api.js';
import { createShell } from './ui/shell.js';
import { generateId } from './utils.js';
import { collectFingerprint, buildCookieSnapshot } from './fingerprint.js';
import { JourneyTracker } from './tracker.js';
import { SessionRecorder } from './replay.js';
import { SurveyManager } from './survey.js';
import { logger } from './logger.js';

const STATES = {
    IDLE: 'idle',
    CONNECTING: 'connecting',
    TEXT_CHAT: 'text-chat',
    VOICE: 'voice',
};

export class Widget {
    constructor(options) {
        this.options = options;
        this.state = STATES.IDLE;
        this.config = null;
        this.api = new ApiClient(options);
        this.lk = null;          // LiveKit connection (lazy)
        this.shell = null;       // UI shell
        this.roomName = null;
        this.messages = [];
        this._currentMode = 'text';  // Track current mode
        this._typingTimer = null;    // Typing debounce

        // Initialize identity synchronously so recording & tracking never have null visitorId
        try {
            this.identity = localStorage.getItem('__gr_vid') || null;
        } catch {
            this.identity = null;
        }
        if (!this.identity) {
            this.identity = `vid_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
            try { localStorage.setItem('__gr_vid', this.identity); } catch {}
        }

        // Register unload handler — sendBeacon is the only reliable way to
        // notify the server during page refresh / tab close / navigation away.
        this._onBeforeUnload = () => {
            if (this.roomName && this.identity) {
                this.api.sendSessionEndedBeacon({
                    visitorId: this.identity,
                    channel: this._currentMode === 'voice' ? 'voice' : 'chat',
                    roomName: this.roomName,
                });
            }
        };
        window.addEventListener('beforeunload', this._onBeforeUnload);
    }

    // ── Lifecycle ───────────────────────────────────

    mount() {
        this.shell = createShell(this);

        const targetEl = this.options.target
            ? (typeof this.options.target === 'string' ? document.querySelector(this.options.target) : this.options.target)
            : null;

        if (targetEl) {
            targetEl.appendChild(this.shell.container);
            if (this.options.mode === 'embedded') {
                this.shell.container.style.position = 'relative';
                this.shell.container.style.bottom = 'auto';
                this.shell.container.style.right = 'auto';
                this.shell.container.style.left = 'auto';
                this.shell.container.style.top = 'auto';
            } else {
                this.shell.container.classList.add('is-contained');
                if (this.shell.host) this.shell.host.classList.add('is-contained');
            }
        } else {
            document.body.appendChild(this.shell.container);
        }

        // Pre-fetch config to position the bubble correctly from the start
        this.api.fetchConfig().then((cfg) => {
            this.config = cfg;

            // If the domain isn't allowed, remove the widget entirely
            if (!this._isDomainAllowed()) {
                logger.warn('[iGreet] Widget blocked: domain not authorized.', {
                    hostname: window.location.hostname,
                    allowed: cfg.allowedDomains,
                });
                this.destroy();
                return;
            }

            logger.info('[iGreet] Config pre-fetched:', { position: cfg?.theme?.position, chatEnabled: cfg?.chatEnabled, journeyEnabled: cfg?.journeyEnabled });

            // ── Chat widget: show or hide the bubble based on service toggle ──
            if (cfg.chatEnabled === false) {
                // Hide the chat bubble entirely — no chat UI
                this.shell?.container?.remove();
                this.shell = null;
                logger.info('[iGreet] Chat widget disabled — bubble hidden.');
            } else {
                this.shell?.applyPosition?.(cfg);

                const isEmbedded = Boolean(this.options.mode === 'embedded' || (!this.options.mode && cfg?.widgetType === 'embedded'));
                if (isEmbedded) {
                    this.shell?.container?.classList.add('is-embedded');
                    if (this.shell?.host) this.shell.host.classList.add('is-embedded');
                    this.open();
                }
            }

            // ── Identify consumer eagerly (needed for both tracker and chat) ──
            const identifyPromise = this._identifyConsumer();

            // ── Journey tracker & Telemetry Suite: start if enabled (independent of chat) ──
            const journeyEnabled = cfg.journeyEnabled !== false
                && this.options.journey?.enabled !== false;

            if (journeyEnabled) {
                const serverUrl = this.options.configUrl || this.options.server || this.options.serverUrl || (typeof document !== 'undefined' ? (document.querySelector('script[data-server]')?.getAttribute('data-server') || window.location.origin) : window.location.origin);

                this.tracker = new JourneyTracker({
                    appId: this.options.appId,
                    apiUrl: serverUrl,
                    visitorId: this.identity,
                    journey: this.options.journey,
                });
                this.tracker.start();

                const sessionId = this.tracker.sessionId || `s_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

                // Start Session Replay recording immediately without blocking on identify
                const replayEnabled = cfg.replayEnabled !== false && this.options.replay?.enabled !== false;
                if (replayEnabled) {
                    this.recorder = new SessionRecorder({
                        appId: this.options.appId,
                        serverUrl,
                        sessionId,
                        visitorId: this.identity,
                        ...this.options.replay,
                    });
                    this.recorder.start();
                }

                // Initialize Click Map & Scroll Depth Telemetry
                this._initHeatmapTracker(serverUrl, sessionId);

                // Initialize Rage Click & User Frustration Detector
                this._initFrustrationDetector(serverUrl, sessionId);

                // Initialize In-App Micro-Surveys & Exit-Intent Polls
                this._initSurveyManager(serverUrl, sessionId);
            }
        }).catch((err) => {
            // Config fetch failed (404/network error) — remove the widget
            logger.warn('[iGreet] Widget blocked — config fetch failed:', err.message);
            this.destroy();
        });
    }

    destroy() {
        window.removeEventListener('beforeunload', this._onBeforeUnload);
        if (this._onHeatmapClick) {
            window.removeEventListener('click', this._onHeatmapClick, true);
        }
        if (this._onHeatmapScroll) {
            window.removeEventListener('scroll', this._onHeatmapScroll);
        }
        if (this._onFrustrationClick) {
            window.removeEventListener('click', this._onFrustrationClick, true);
        }
        if (this._onFrustrationError) {
            window.removeEventListener('error', this._onFrustrationError);
        }
        if (this._onFrustrationRejection) {
            window.removeEventListener('unhandledrejection', this._onFrustrationRejection);
        }
        this._disconnect();
        if (this.recorder) {
            this.recorder.stop();
            this.recorder = null;
        }
        if (this.tracker) {
            this.tracker.destroy();
            this.tracker = null;
        }
        if (this.surveyManager) {
            this.surveyManager.destroy();
            this.surveyManager = null;
        }
        if (this.shell?.container?.parentNode) {
            this.shell.container.parentNode.removeChild(this.shell.container);
        }
        this.shell = null;
        this.state = STATES.IDLE;
        this._onDestroy?.();
    }

    // ── State Transitions ──────────────────────────

    async open() {
        if (this.state !== STATES.IDLE) return;
        this._setState(STATES.CONNECTING);

        try {
            // Step 1: Identify consumer via fingerprint matching
            if (!this.identity) {
                await this._identifyConsumer();
            }

            // Step 2: Fetch config if not cached
            if (!this.config) {
                this.config = await this.api.fetchConfig();
            }

            // Step 3: Domain restriction check
            if (!this._isDomainAllowed()) {
                logger.warn('[iGreet] Widget blocked: current domain is not in allowedDomains.',
                    { hostname: window.location.hostname, allowed: this.config.allowedDomains });
                this._setState(STATES.IDLE);
                return;
            }

            this.shell?.showPanel(this.config);
            this._setState(STATES.TEXT_CHAT);

            // Step 4: Connect to LiveKit — agent sends firstMessage on join
            this._connect();
        } catch (err) {
            logger.error('[iGreet] Failed to open widget:', err);
            this._setState(STATES.IDLE);
        }
    }

    close() {
        this._disconnect();
        this.shell?.hidePanel();
        this._setState(STATES.IDLE);
    }

    async sendMessage(text) {
        logger.info('[iGreet] sendMessage called with text:', text, new Error().stack);
        if (!text?.trim()) return;
        const msg = { role: 'user', content: text.trim(), time: new Date() };
        this.messages.push(msg);
        this.shell?.addMessage(msg);

        // Show typing indicator while bot/agent processes the message
        this.shell?.showTyping?.();

        // Connect to LiveKit on first message
        if (!this.lk) {
            await this._connect();
        }

        // Send via text stream (topic: 'message')
        if (this.lk) {
            await this.lk.sendChatMessage(text.trim());
        }

        // Persist to inbox (fire-and-forget)
        this.api.persistInteraction({
            visitorId: this.identity,
            role: 'consumer',
            content: text.trim(),
        });
    }

    /**
     * Notify the server that the consumer is typing.
     * Debounced: fires at most once every 2 seconds.
     */
    notifyTyping() {
        if (!this.identity || this._typingTimer) return;
        this._typingTimer = setTimeout(() => { this._typingTimer = null; }, 2000);
        this.api.notifyTyping({ visitorId: this.identity });
    }

    /**
     * Set mode: 'text' or 'voice'
     * Handles LiveKit mic/audio and UI in one place
     */
    async setMode(mode) {
        if (mode === this._currentMode) return;
        this._currentMode = mode;

        if (mode === 'voice') {
            // Connect to LiveKit if needed, but do NOT enable mic yet.
            // User must click the grey mic button to start listening.
            if (!this.lk) {
                await this._connect();
            }
            this._setState(STATES.VOICE);
            this.shell?.setMode('voice');
        } else {
            // Text mode: disable mic, mute agent audio
            if (this.lk) {
                await this.lk.disableMicrophone();
                this.lk.muteAgentAudio();
            }
            this._setState(STATES.TEXT_CHAT);
            this.shell?.setMode('text');
        }

        // Notify server of mode switch (SSE → messaging page updates takeover button type)
        if (this.roomName) {
            this.api.notifySessionStatus({
                visitorId: this.identity,
                status: 'active',
                channel: mode === 'voice' ? 'voice' : 'chat',
                roomName: this.roomName,
            });
        }
    }

    /**
     * Toggle mic listening on/off (within voice mode)
     * Also controls agent audio: unmuted when mic is on, muted when off
     */
    async toggleMic() {
        if (!this.lk) {
            await this._connect();
        }
        if (this.lk) {
            const isEnabled = this.lk.isMicEnabled();
            if (isEnabled) {
                await this.lk.disableMicrophone();
                this.lk.muteAgentAudio();
            } else {
                await this.lk.enableMicrophone();
                this.lk.unmuteAgentAudio();
            }
            return !isEnabled;  // return new listening state
        }
        return false;
    }

    // Back-compat
    async toggleVoice() {
        if (this.state === STATES.TEXT_CHAT) {
            await this.setMode('voice');
        } else if (this.state === STATES.VOICE) {
            await this.setMode('text');
        }
    }

    // ── Private ────────────────────────────────────

    /**
     * Check if the current page's domain is allowed to run this widget.
     * Returns true if: allowedDomains is empty, hostname is localhost/127.0.0.1,
     * or hostname matches (or is a subdomain of) an allowed domain.
     */
    _isDomainAllowed() {
        const allowed = this.config?.allowedDomains;
        if (!allowed || allowed.length === 0) return true; // No restrictions

        const hostname = window.location.hostname.toLowerCase();

        // Always allow localhost and the dashboard for development/testing
        // #TODOS: ADD ON DB OR ENV
        if (hostname === 'localhost' || hostname === '127.0.0.1'
            || hostname === 'customers.new' || hostname === 'app.customers.new') return true;

        // Normalize allowed entries to hostnames
        const normalizedAllowed = allowed.map((entry) => {
            try {
                if (entry.includes('://')) return new URL(entry).hostname.toLowerCase();
                return entry.trim().toLowerCase().replace(/\/+$/, '');
            } catch {
                return entry.trim().toLowerCase();
            }
        });

        // Exact match or subdomain match
        return normalizedAllowed.some((ah) =>
            hostname === ah || hostname.endsWith('.' + ah)
        );
    }

    _setState(newState) {
        const prev = this.state;
        this.state = newState;
        this.shell?.onStateChange?.(newState, prev);
    }

    /**
     * Identify the consumer via fingerprint matching.
     * Called eagerly on mount so the tracker gets the stable visitorId.
     * Safe to call multiple times — no-ops if already identified.
     */
    async _identifyConsumer() {
        if (this._isIdentified) return;
        try {
            const rawJsFp = await collectFingerprint();
            const cookieSnapshot = buildCookieSnapshot();
            const result = await this.api.identify({ rawJsFp, cookieSnapshot });
            this._isIdentified = true;
            this.identity = result.visitorId;
            // Persist to localStorage so the tracker reuses this ID on future page loads
            try { localStorage.setItem('__gr_vid', result.visitorId); } catch {}
            // Update tracker and recorder if they were already created
            if (this.tracker) {
                this.tracker.visitorId = result.visitorId;
            }
            if (this.recorder) {
                this.recorder.visitorId = result.visitorId;
            }
            logger.info('[iGreet] Identified:', {
                visitorId: result.visitorId,
                isNew: result.isNew,
                confidence: result.confidence,
            });
        } catch (err) {
            logger.warn('[iGreet] Identification failed, using fallback:', err.message);
            this.identity = `visitor_${generateId()}`;
        }
    }

    async _connect() {
        if (this.lk) return;

        // Show loading immediately
        this.shell?.onLiveKitState?.('connecting');

        try {
            this.roomName = `widget_${this.config?.appId || this.options.appId}_${generateId()}`;

            // Fetch LiveKit token via server-side proxy
            const tokenData = await this.api.fetchToken({
                identity: this.identity,
                roomName: this.roomName,
            });

            logger.info('[iGreet] Connecting to LiveKit...', { url: tokenData.url, room: this.roomName });

            // Lazy-load LiveKit module
            const { LiveKitConnection } = await import('./livekit.js');
            this.lk = new LiveKitConnection({
                token: tokenData.token,
                url: tokenData.url,
                onMessage: (msg) => {
                    logger.info('[iGreet] onMessage received msg:', msg);
                    if (msg.role === 'user' && this._currentMode !== 'voice') {
                        logger.info('[iGreet] Suppressing duplicate user message in text mode:', msg.content);
                        return;
                    }
                    this.messages.push(msg);
                    this.shell?.hideTyping?.(); // Hide typing indicator when response arrives
                    this.shell?.addMessage(msg);

                    // Persist messages to inbox (fire-and-forget)
                    // Bot/agent/assistant messages are always persisted here.
                    // User messages: text-mode ones are persisted in sendMessage(),
                    // but voice-mode transcriptions only arrive here, so persist them too.
                    if (msg.content) {
                        if (msg.role && msg.role !== 'user') {
                            this.api.persistInteraction({
                                visitorId: this.identity,
                                role: msg.role === 'assistant' ? 'bot' : msg.role,
                                content: msg.content,
                            });
                        } else if (msg.role === 'user' && this._currentMode === 'voice') {
                            // Voice transcription — user spoke, persist to inbox
                            this.api.persistInteraction({
                                visitorId: this.identity,
                                role: 'consumer',
                                content: msg.content,
                                type: 'voice',
                            });
                        }
                    }
                },
                onStateChange: (state) => {
                    logger.info('[iGreet] LiveKit state:', state);
                    this.shell?.onLiveKitState?.(state);
                },
                onTyping: () => {
                    this.shell?.showTyping?.();
                },
            });

            await this.lk.connect();
            logger.info('[iGreet] Connected to LiveKit room:', this.roomName);

            // send empty message upon connection to start widget chat bot
            this.lk.sendChatMessage('');

            // Notify server that session is active (triggers SSE → messaging page shows Takeover)
            this.api.notifySessionStatus({
                visitorId: this.identity,
                status: 'active',
                channel: this._currentMode === 'voice' ? 'voice' : 'chat',
                roomName: this.roomName,
            });

            // Hide loading
            this.shell?.onLiveKitState?.('connected');

            // If in text mode, mute agent audio by default
            if (this._currentMode === 'text') {
                this.lk.muteAgentAudio();
            }
        } catch (err) {
            logger.error('[iGreet] Failed to connect:', err);
            this.shell?.onLiveKitState?.('disconnected');
            this.lk = null;
        }
    }

    _disconnect() {
        if (this.lk) {
            this.lk.disconnect();
            this.lk = null;
        }
        // Notify server that session ended (triggers SSE → messaging page hides Takeover)
        if (this.roomName) {
            this.api.notifySessionStatus({
                visitorId: this.identity,
                status: 'ended',
                channel: this._currentMode === 'voice' ? 'voice' : 'chat',
                roomName: this.roomName,
            });
        }
    }

    /**
     * Identify or associate current visitor with a B2B Group / Account (PostHog-style)
     * @param {string} groupType e.g. 'company', 'organization', 'workspace'
     * @param {string} groupKey e.g. 'acme_corp', 'cyberdyne.io'
     * @param {object} properties e.g. { name: 'Acme Corp', plan: 'enterprise', mrr: 2500 }
     */
    group(groupType = 'company', groupKey, properties = {}) {
        if (!groupKey) return;
        const payload = {
            groupType,
            groupKey: String(groupKey).trim(),
            properties: properties || {},
        };
        this._groupKey = payload.groupKey;
        this._groupPlan = properties?.plan || '';
        // If replay recorder active, store group tag in session metadata
        if (this._recorder) {
            this._recorder.groupKey = payload.groupKey;
            this._recorder.groupType = payload.groupType;
        }
        if (typeof this.reloadFeatureFlags === 'function') {
            this.reloadFeatureFlags();
        }
        return payload;
    }

    _initHeatmapTracker(serverUrl, sessionId) {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        // Auto-resolve API server origin (e.g. http://localhost:7654)
        let effectiveServerUrl = (serverUrl || this.options.configUrl || '').replace(/\/$/, '');
        if (!effectiveServerUrl) {
            const script = document.currentScript || document.querySelector('script[data-app-id]');
            if (script && script.src) {
                try {
                    effectiveServerUrl = new URL(script.src, window.location.href).origin;
                } catch {}
            }
        }
        if (!effectiveServerUrl) {
            effectiveServerUrl = window.location.origin;
        }

        const getDevice = () => {
            const width = window.innerWidth || 1440;
            if (width < 640) return 'mobile';
            if (width < 1024) return 'tablet';
            return 'desktop';
        };

        const currentUrl = window.location.pathname || '/';
        let clickBuffer = [];
        let flushTimer = null;

        const flushClicks = () => {
            if (clickBuffer.length === 0) return;
            const payload = {
                businessId: this.options.appId,
                appId: this.options.appId,
                clicks: [...clickBuffer],
            };
            clickBuffer = [];
            try {
                fetch(`${effectiveServerUrl}/api/heatmaps/events`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    keepalive: true,
                }).catch(() => {});
            } catch {}
        };

        // Capture Clicks
        this._onHeatmapClick = (e) => {
            try {
                const scrollW = Math.max(1, document.documentElement.scrollWidth || window.innerWidth);
                const scrollH = Math.max(1, document.documentElement.scrollHeight || window.innerHeight);

                const xPercent = Math.max(0, Math.min(1, (e.pageX || e.clientX) / scrollW));
                const yPercent = Math.max(0, Math.min(1, (e.pageY || e.clientY) / scrollH));

                const target = e.target;
                const targetTag = target?.tagName || '';
                const targetText = (target?.innerText || target?.textContent || '').trim().slice(0, 100);
                const targetSelector = target?.id ? `#${target.id}` : target?.className ? `.${String(target.className).split(' ')[0]}` : targetTag;

                const rawPageX = Math.round(e.pageX || e.clientX || 0);
                const rawPageY = Math.round(e.pageY || e.clientY || 0);
                const vw = Math.round(window.innerWidth || 1440);
                const vh = Math.round(window.innerHeight || 900);

                clickBuffer.push({
                    url: currentUrl,
                    origin: window.location.origin || '',
                    sessionId,
                    visitorId: this.identity || 'anonymous',
                    device: getDevice(),
                    xPercent,
                    yPercent,
                    pageX: rawPageX,
                    pageY: rawPageY,
                    viewportWidth: vw,
                    viewportHeight: vh,
                    documentWidth: scrollW,
                    documentHeight: scrollH,
                    targetTag,
                    targetText,
                    targetSelector,
                });

                if (!flushTimer) {
                    flushTimer = setTimeout(() => {
                        flushTimer = null;
                        flushClicks();
                    }, 400);
                }
            } catch {}
        };

        window.addEventListener('click', this._onHeatmapClick, true);
        window.addEventListener('beforeunload', flushClicks);

        // Capture Scroll Depth Milestones
        let maxDepth = 0;
        let scrollTimer = null;

        this._onHeatmapScroll = () => {
            if (scrollTimer) return;
            scrollTimer = setTimeout(() => {
                scrollTimer = null;
                try {
                    const scrollH = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
                    const currentDepth = Math.min(100, Math.round(((window.scrollY || window.pageYOffset || 0) / (scrollH || 1)) * 100));

                    if (currentDepth > maxDepth + 10 || (currentDepth >= 90 && maxDepth < 90)) {
                        maxDepth = Math.max(maxDepth, currentDepth);
                        fetch(`${effectiveServerUrl}/api/heatmaps/events`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                businessId: this.options.appId,
                                appId: this.options.appId,
                                scrolls: [{
                                    url: currentUrl,
                                    origin: window.location.origin || '',
                                    sessionId,
                                    visitorId: this.identity || 'anonymous',
                                    device: getDevice(),
                                    depthPercent: maxDepth,
                                }],
                            }),
                            keepalive: true,
                        }).catch(() => {});
                    }
                } catch {}
            }, 500);
        };

        window.addEventListener('scroll', this._onHeatmapScroll, { passive: true });
    }

    // ── Rage Click & User Frustration Detection Engine ──────────────
    _initFrustrationDetector(serverUrl, sessionId) {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        let effectiveServerUrl = (serverUrl || this.options.configUrl || '').replace(/\/$/, '');
        if (!effectiveServerUrl) {
            const script = document.currentScript || document.querySelector('script[data-app-id]');
            if (script && script.src) {
                try { effectiveServerUrl = new URL(script.src, window.location.href).origin; } catch {}
            }
        }
        if (!effectiveServerUrl) {
            effectiveServerUrl = window.location.origin;
        }

        const getDevice = () => {
            const width = window.innerWidth || 1440;
            if (width < 640) return 'mobile';
            if (width < 1024) return 'tablet';
            return 'desktop';
        };

        const getSelector = (el) => {
            if (!el || el === document.body || el === document.documentElement) return 'body';
            if (el.id) return `#${el.id}`;
            const parts = [];
            let curr = el;
            while (curr && curr !== document.body && parts.length < 3) {
                let name = curr.tagName ? curr.tagName.toLowerCase() : '';
                if (curr.className && typeof curr.className === 'string') {
                    const primaryClass = curr.className.split(/\s+/).filter(Boolean)[0];
                    if (primaryClass) name += `.${primaryClass}`;
                }
                parts.unshift(name);
                curr = curr.parentElement;
            }
            return parts.join(' > ') || el.tagName.toLowerCase();
        };

        let frustrationQueue = [];
        let flushTimer = null;

        const flushFrustrations = () => {
            if (frustrationQueue.length === 0) return;
            const payload = {
                businessId: this.options.appId,
                appId: this.options.appId,
                events: [...frustrationQueue],
            };
            frustrationQueue = [];
            try {
                fetch(`${effectiveServerUrl}/api/frustration/events`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    keepalive: true,
                }).catch(() => {});
            } catch {}
        };

        const scheduleFlush = () => {
            if (!flushTimer) {
                flushTimer = setTimeout(() => {
                    flushTimer = null;
                    flushFrustrations();
                }, 800);
            }
        };

        // Track DOM mutations for Dead Click detection
        let lastMutationTime = 0;
        if (typeof MutationObserver !== 'undefined' && document.body) {
            try {
                const domObserver = new MutationObserver(() => {
                    lastMutationTime = Date.now();
                });
                domObserver.observe(document.body, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    characterData: true,
                });
            } catch {}
        }

        // Rolling click history for rage detection: array of { time, x, y, selector, tag, text }
        let clickHistory = [];
        let lastReportedRageTime = 0;
        let lastClickInfo = null;

        this._onFrustrationClick = (e) => {
            try {
                const now = Date.now();
                const rawTarget = e.target;
                const interactiveEl = rawTarget.closest('button, a, [role="button"], input, select, .capability-tab, .tab, .btn, .card') || rawTarget;
                const target = interactiveEl;
                const pageX = e.pageX ?? e.clientX;
                const pageY = e.pageY ?? e.clientY;
                const selector = getSelector(target);
                const tag = target.tagName ? target.tagName.toUpperCase() : '';
                const text = (target.innerText || target.value || target.getAttribute('aria-label') || '').slice(0, 100).trim();

                const currentClick = {
                    time: now,
                    x: pageX,
                    y: pageY,
                    selector,
                    tag,
                    text,
                    url: window.location.pathname || '/',
                };
                lastClickInfo = currentClick;

                // 1. Rage Click Detection: 3+ clicks within 1500ms on same target or within 60px radius
                clickHistory.push(currentClick);
                clickHistory = clickHistory.filter((c) => now - c.time <= 1500);

                if (clickHistory.length >= 3 && now - lastReportedRageTime > 800) {
                    const recentClicks = clickHistory.slice(-6);
                    const firstClick = recentClicks[0];
                    const timeSpan = now - firstClick.time;

                    const allClose = recentClicks.every((c) => {
                        const dist = Math.sqrt(Math.pow(c.x - firstClick.x, 2) + Math.pow(c.y - firstClick.y, 2));
                        return dist <= 60 || c.selector === firstClick.selector;
                    });

                    if (allClose && timeSpan <= 1500) {
                        lastReportedRageTime = now;
                        console.log('[iGreet Telemetry] 🔥 Rage Click Captured:', {
                            selector,
                            text,
                            clickCount: recentClicks.length,
                            timeSpanMs: Math.round(timeSpan),
                        });
                        frustrationQueue.push({
                            sessionId,
                            visitorId: this.identity || 'anonymous',
                            groupKey: this._groupKey || '',
                            type: 'rage_click',
                            url: window.location.pathname || '/',
                            targetSelector: selector,
                            targetTag: tag,
                            targetText: text,
                            pageX: Math.round(pageX),
                            pageY: Math.round(pageY),
                            clickCount: recentClicks.length,
                            timeSpanMs: Math.round(timeSpan),
                            device: getDevice(),
                            timestamp: new Date().toISOString(),
                        });
                        scheduleFlush();
                    }
                }

                // 2. Dead Click Detection: clicking interactive/clickable element that produces zero UI response
                const isInteractive = target.closest('button, a, [role="button"], input[type="submit"], input[type="button"], .btn, [onclick]');
                const computedCursor = (typeof window !== 'undefined' && window.getComputedStyle) ? window.getComputedStyle(target).cursor : '';
                const appearsClickable = isInteractive || computedCursor === 'pointer';

                if (appearsClickable && !target.closest('.igreet-widget-container')) {
                    const initialHref = window.location.href;
                    const clickTime = now;
                    const candidate = currentClick;

                    setTimeout(() => {
                        try {
                            const noUrlChange = window.location.href === initialHref;
                            const noDomChange = lastMutationTime < clickTime;
                            if (noUrlChange && noDomChange && Date.now() - lastReportedRageTime > 1500) {
                                console.log('[iGreet Telemetry] 🛑 Dead Click Captured:', {
                                    selector: candidate.selector,
                                    text: candidate.text,
                                    tag: candidate.tag,
                                });
                                frustrationQueue.push({
                                    sessionId,
                                    visitorId: this.identity || 'anonymous',
                                    groupKey: this._groupKey || '',
                                    type: 'dead_click',
                                    url: candidate.url,
                                    targetSelector: candidate.selector,
                                    targetTag: candidate.tag,
                                    targetText: candidate.text,
                                    pageX: Math.round(candidate.x),
                                    pageY: Math.round(candidate.y),
                                    device: getDevice(),
                                    timestamp: new Date().toISOString(),
                                });
                                scheduleFlush();
                            }
                        } catch {}
                    }, 1200);
                }
            } catch {}
        };

        // 3. Error Click Detection: unhandled JS runtime exceptions within 1500ms of a click
        this._onFrustrationError = (evt) => {
            try {
                if (lastClickInfo && Date.now() - lastClickInfo.time <= 1500) {
                    const errMsg = evt.message || (evt.error && evt.error.message) || 'Unhandled runtime error';
                    const errStack = (evt.error && evt.error.stack) || '';
                    frustrationQueue.push({
                        sessionId,
                        visitorId: this.identity || 'anonymous',
                        groupKey: this._groupKey || '',
                        type: 'error_click',
                        url: lastClickInfo.url,
                        targetSelector: lastClickInfo.selector,
                        targetTag: lastClickInfo.tag,
                        targetText: lastClickInfo.text,
                        pageX: Math.round(lastClickInfo.x),
                        pageY: Math.round(lastClickInfo.y),
                        errorMessage: errMsg,
                        errorStack: errStack,
                        device: getDevice(),
                        timestamp: new Date().toISOString(),
                    });
                    lastClickInfo = null;
                    scheduleFlush();
                }
            } catch {}
        };

        this._onFrustrationRejection = (evt) => {
            try {
                if (lastClickInfo && Date.now() - lastClickInfo.time <= 1500) {
                    const errMsg = evt.reason ? (evt.reason.message || String(evt.reason)) : 'Unhandled Promise Rejection';
                    frustrationQueue.push({
                        sessionId,
                        visitorId: this.identity || 'anonymous',
                        groupKey: this._groupKey || '',
                        type: 'error_click',
                        url: lastClickInfo.url,
                        targetSelector: lastClickInfo.selector,
                        targetTag: lastClickInfo.tag,
                        targetText: lastClickInfo.text,
                        pageX: Math.round(lastClickInfo.x),
                        pageY: Math.round(lastClickInfo.y),
                        errorMessage: errMsg,
                        device: getDevice(),
                        timestamp: new Date().toISOString(),
                    });
                    lastClickInfo = null;
                    scheduleFlush();
                }
            } catch {}
        };

        window.addEventListener('click', this._onFrustrationClick, true);
        window.addEventListener('error', this._onFrustrationError);
        window.addEventListener('unhandledrejection', this._onFrustrationRejection);
        window.addEventListener('beforeunload', flushFrustrations);
    }

    _initSurveyManager(serverUrl, sessionId) {
        try {
            this.surveyManager = new SurveyManager({
                appId: this.options.appId,
                serverUrl,
                visitorId: this.identity || 'anonymous',
                sessionId,
            });
            this.surveyManager.init();
        } catch (err) {
            console.debug('[iGreet] SurveyManager init error:', err.message);
        }
    }
}
